import { useQuery } from '@tanstack/react-query'
import { BoosterProgramDetail } from 'Modules'

import { AppAdmin } from 'api/app-admin'
import {
  mapApiItemToFormValues,
  mapApiToBoosterProgramDetail,
} from 'modules/booster-program/api-adapters'
import { getApiErrorMessage } from 'utils/api-error'
import { logError } from 'utils/logger'

import { boosterProgramKeys } from './query-keys'

const fetchBoosterProgramDetail = async (id: string): Promise<BoosterProgramDetail | null> => {
  try {
    const res = await AppAdmin.getBoosterProgramDetail(id)
    if (!res.data?.success || !res.data.data) return null
    return mapApiToBoosterProgramDetail(res.data.data)
  } catch (error) {
    logError('Error fetching booster program detail:', error)
    throw new Error(getApiErrorMessage(error, 'Failed to load booster program'))
  }
}

export const useBoosterProgramDetail = (
  id: string | undefined
): {
  detail: BoosterProgramDetail | null
  isLoading: boolean
  error: unknown
} => {
  const query = useQuery({
    queryKey: boosterProgramKeys.detail(id),
    queryFn: () => fetchBoosterProgramDetail(id!),
    enabled: !!id,
  })

  return {
    detail: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  }
}

/**
 * Loads a booster program by id and maps it to form values for the create/edit
 * form. Disabled when `id` is undefined (i.e., creating a new program).
 */
export const useBoosterProgramDraftFormValues = (id: string | undefined) => {
  const query = useQuery({
    queryKey: [...boosterProgramKeys.detail(id), 'formValues'],
    queryFn: async () => {
      const res = await AppAdmin.getBoosterProgramDetail(id!)
      if (!res.data?.success || !res.data.data) return undefined
      return mapApiItemToFormValues(res.data.data)
    },
    enabled: !!id,
  })

  return {
    initialValues: query.data,
    isLoading: query.isLoading,
  }
}
